<template>
  <fieldset>
    <legend>
      {{ $t('Add your umbrella company details') }}
    </legend>

    <WdsAlert v-if="complianceData.hasHireNotYetAccepted" info class="m-b-10" :title="$t('Note')">
      <template #description>
        <p>
          You have a draft contract based on a setup, where you're
          <u>not</u>
          using an Umbrella service. If you change your setup to use an Umbrella service, we will
          automatically update this contract to reflect your updated business setup.
        </p>
      </template>
    </WdsAlert>

    <WdsAlert warning>
      <template #description>
        <p>
          Be aware that we only work with Umbrella companies that have been accredited by either
          <a href="https://www.fcsa.org.uk/members/" target="_blank">FSCA</a>
          or
          <a
            href="https://www.professionalpassport.com/Approved-Providers/Approved-Providers"
            target="_blank"
          >
            Professional Passport
          </a>
          .
        </p>
      </template>
    </WdsAlert>

    <BusinessEntityInput
      v-model="umbrellaCompanyName"
      :disabled="disabled"
      :label="$t('Their company name')"
      name="umbrella_company_name"
      :placeholder="$t('e.g. Worksome ApS')"
    />

    <BusinessEntityInput
      v-model="umbrellaCompanyNo"
      :disabled="disabled"
      :label="$t('Their company registration number')"
      name="umbrella_company_no"
      :placeholder="$t('e.g. 26584902')"
    >
      <CompanyNumberTooltip class="m-l-5" market="UK" />
    </BusinessEntityInput>

    <BusinessEntityInput
      v-model="umbrellaAddress"
      :disabled="disabled"
      :label="$t('Their address')"
      name="umbrella_address"
      :placeholder="$t('Street name')"
    />

    <div class="row m-b-10">
      <div class="col-xs-7">
        <BusinessEntityInput
          v-model="umbrellaCity"
          :disabled="disabled"
          :label="$t('Their city')"
          name="umbrella_city"
          :placeholder="$t('City')"
        />
      </div>

      <div class="col-xs-5">
        <BusinessEntityInput
          v-model="umbrellaZipcode"
          :disabled="disabled"
          :label="$t('Their post code')"
          name="umbrella_zipcode"
          :placeholder="$t('Post code')"
        />
      </div>
    </div>

    <div>
      <label for="umbrella_country">
        {{ $t('Their country') }}
      </label>
      <select
        v-model="umbrellaCountry"
        class="form-control"
        :disabled="disabled"
        name="umbrella_country"
      >
        <option value="">{{ $t('Select country') }}</option>
        <option v-for="{ code, name } in freelancer.countries" :key="code" :value="code">
          {{ name }}
        </option>
      </select>
    </div>

    <div class="m-t-5">
      <BusinessEntityInput
        v-model="umbrellaVatNo"
        :disabled="disabled"
        :label="$t('Their VAT number')"
        name="umbrella_vat_no"
        :placeholder="$t('VAT no.')"
      />
    </div>

    <WdsAlert v-if="complianceData.hasValidBankDetails" warning class="m-t-10 m-b-10">
      <template #description>
        {{
          $t(
            'Make sure to also update your bank details to those of your umbrella company before any payouts.',
          )
        }}
      </template>
    </WdsAlert>
  </fieldset>
</template>

<script>
/* eslint-disable import/extensions */
import { WdsAlert } from '@worksome/wds-components'
import gql from 'graphql-tag'
import BusinessEntityInput from '@/components/compliances/BusinessEntity/BusinessEntityInput.vue'
import CompanyNumberTooltip from '@/components/compliances/BusinessEntity/CompanyNumberTooltip.vue'
import { deepClone, setdefault } from '@/utils/tools'

export const fragments = {
  account: gql`
    fragment UkBusinessEntityUmbrellaFormAccount on Account {
      countries {
        code
        name
      }
    }
  `,
}

export default {
  name: 'UkBusinessEntityUmbrellaForm',
  components: {
    BusinessEntityInput,
    CompanyNumberTooltip,
    WdsAlert,
  },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    freelancer: {
      type: Object,
      required: true,
    },
  },
  data() {
    const internalBusinessEntity = deepClone(this.businessEntity)
    return {
      internalBusinessEntity,
    }
  },
  computed: {
    umbrellaAddress: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_address
      },
      set(umbrellaAddress) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_address = umbrellaAddress
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    umbrellaCity: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_city
      },
      set(umbrellaCity) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_city = umbrellaCity
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    umbrellaCompanyName: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_company_name
      },
      set(umbrellaCompanyName) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_company_name = umbrellaCompanyName
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    umbrellaCompanyNo: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_company_no
      },
      set(umbrellaCompanyNo) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_company_no = umbrellaCompanyNo
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    umbrellaCountry: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_country ?? ''
      },
      set(umbrellaCountry) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_country = umbrellaCountry
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    umbrellaVatNo: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_vat_no
      },
      set(umbrellaVatNo) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_vat_no = umbrellaVatNo
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
    umbrellaZipcode: {
      get() {
        return this.internalBusinessEntity.data?.umbrella?.umbrella_zipcode
      },
      set(umbrellaZipcode) {
        const data = setdefault(this.internalBusinessEntity, 'data', {})
        const umbrellaData = setdefault(data, 'umbrella', {})
        umbrellaData.umbrella_zipcode = umbrellaZipcode
        this.$emit('update:businessEntity', this.internalBusinessEntity)
      },
    },
  },
}
</script>
